'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Package,
  CheckCircle2,
  XCircle,
  Clock,
  ScanLine,
  Search
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import type { SortingSessionItem } from '../types';

interface SortingContainerListProps {
  items: SortingSessionItem[];
  selectedItemId: number | null;
  onSelectItem: (item: SortingSessionItem) => void;
  totalContainers: number;
  sortedContainers: number;
  refusedContainers: number;
  className?: string;
}

const statusConfig = {
  pending: {
    icon: Clock,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/30',
    border: 'border-amber-200 dark:border-amber-800',
    labelKey: 'statusPending' as const
  },
  scanning: {
    icon: ScanLine,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/30',
    border: 'border-blue-200 dark:border-blue-800',
    labelKey: 'statusScanning' as const
  },
  scanned: {
    icon: CheckCircle2,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-900/30',
    border: 'border-green-200 dark:border-green-800',
    labelKey: 'statusCompleted' as const
  },
  refused: {
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/30',
    border: 'border-red-200 dark:border-red-800',
    labelKey: 'moveContainer' as const
  }
} as const;

export function SortingContainerList({
  items,
  selectedItemId,
  onSelectItem,
  totalContainers,
  sortedContainers,
  refusedContainers,
  className
}: SortingContainerListProps) {
  const t = useTranslations('sorting');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredItems = searchTerm
    ? items.filter((item) =>
        item.container_number.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : items;

  const processedCount = sortedContainers + refusedContainers;
  const progressPercent =
    totalContainers > 0
      ? Math.round((processedCount / totalContainers) * 100)
      : 0;

  return (
    <Card className={cn('flex flex-col lg:h-full', className)}>
      <CardHeader className='pb-3'>
        <div className='flex items-center justify-between'>
          <CardTitle className='flex items-center gap-2 text-base'>
            <Package className='h-4 w-4' />
            {t('containerList')}
          </CardTitle>
          <Badge variant='secondary'>
            {t('total')}: {totalContainers}
          </Badge>
        </div>
        <div className='mt-2 space-y-1'>
          <div className='text-muted-foreground flex items-center justify-between text-xs'>
            <span>{t('processingProgress')}</span>
            <span>{progressPercent}%</span>
          </div>
          <Progress value={progressPercent} className='h-2' />
        </div>
      </CardHeader>
      <CardContent className='flex flex-col p-0 lg:flex-1 lg:overflow-hidden'>
        <div className='shrink-0 px-4 pt-2 pb-2'>
          <div className='relative'>
            <Search className='text-muted-foreground absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2' />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('searchContainerPlaceholder')}
              className='h-8 ps-9 text-sm'
            />
          </div>
        </div>
        <ScrollArea className='max-h-72 lg:max-h-none lg:flex-1'>
          <div className='space-y-2 px-4 py-4'>
            {filteredItems.map((item) => {
              const config = statusConfig[item.status];
              const StatusIcon = config.icon;
              const isSelected = selectedItemId === item.id;
              const expectedPages = item.registration?.expected_page_count ?? 0;
              const pageProgress =
                item.status === 'scanned'
                  ? 100
                  : expectedPages > 0
                    ? Math.round((item.scanned_pages / expectedPages) * 100)
                    : 0;

              return (
                <button
                  key={item.id}
                  type='button'
                  onClick={() => onSelectItem(item)}
                  className={cn(
                    'w-full rounded-lg border p-3 text-start transition-all',
                    config.bg,
                    config.border,
                    isSelected && 'ring-primary ring-2 ring-offset-1',
                    item.status === 'refused' || item.status === 'scanned'
                      ? 'opacity-70'
                      : 'hover:shadow-sm'
                  )}
                >
                  <div className='flex items-center justify-between'>
                    <span className='text-sm font-medium'>
                      {item.container_number}
                    </span>
                    <StatusIcon
                      className={cn(
                        'h-4 w-4',
                        config.color,
                        item.status === 'scanning' && 'animate-pulse'
                      )}
                    />
                  </div>

                  {/* Page progress */}
                  <div className='mt-2 space-y-1'>
                    <div className='flex items-center justify-between text-xs'>
                      <span className={config.color}>
                        {t('processingProgress')}
                      </span>
                      <span className='text-muted-foreground'>
                        {item.status === 'scanned'
                          ? expectedPages
                          : item.scanned_pages}
                        {expectedPages > 0 ? ` / ${expectedPages}` : ''}
                      </span>
                    </div>
                    <Progress value={pageProgress} className='h-1.5' />
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
